import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts'; // This provides default Roboto fonts as fallback

// Import only the Poppins Regular and Bold base64 font data
// You MUST ensure PoppinsBoldBase64 is the base64 of Poppins-Bold.ttf (700 weight)
import {
  PoppinsSemiBoldBase64,
  PoppinsBoldBase64,
  PoppinsRegularBase64,
} from './font'; // Adjust this path as necessary
import {
  arrowDownBase64,
  iconDatabaseBase64,
  iconSnapshotsBase64,
  iconUnusedIPsBase64,
  iconVolumeBase64,
  logoBase64,
} from './data';

// --- Setup PDFMake VFS with Poppins ---
// eslint-disable-next-line
// @ts-ignore
pdfMake.vfs = pdfFonts || {}; // Standard way to assign default Roboto fonts

// Add your custom Poppins fonts to the VFS
// Ensure these filenames match what you define in pdfMake.fonts below.
pdfMake.vfs['Poppins-Regular.ttf'] = PoppinsRegularBase64;
pdfMake.vfs['Poppins-Bold.ttf'] = PoppinsBoldBase64;
pdfMake.vfs['PoppinsSemiBold-Bold.ttf'] = PoppinsSemiBoldBase64;

// Define the fonts for PDFMake.
pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-Italic.ttf',
  },
  Poppins: {
    normal: 'Poppins-Regular.ttf', // for font-weight 400
    bold: 'Poppins-Bold.ttf', // for font-weight 700 (and general 'bold' property)
  },
  PoppinsSemiBold: {
    normal: 'Poppins-Regular.ttf',
    bold: 'PoppinsSemiBold-Bold.ttf',
  },
};

export interface CostReportData {
  accountName: string;
  reportDate: string;
  generatedBy: string;
  resourceCount: number;
  timeline: string;
  timelineDate: string;

  savingsPercentage: number;
  monthlySaving: number;
  yearlySaving: number;
  monthlySpend: number;
  yearlySpend: number;

  projects: {
    name: string;
    resourceCount: number;
    monthlySpend: number;
    monthlySaving: number;
    savingPercent: number;
  }[];

  resourcesSummary: {
    name: string;
    monthlyCost: number;
    monthlySavings: number;
    yearlyCost: number;
    yearlySavings: number;
  }[];

  recommendations: {
    immediate: string[];
    midTerm: string[];
    longTerm: string[];
  };

  complexity: {
    name: string;
    level: 'Low' | 'Medium' | 'High';
  }[];
}

function roundToOneDecimal(num: number) {
  // Multiply by 10, round, then divide by 10
  return Math.round(num * 10) / 10;
}

export const generateCostReportPDF = async (
  data: CostReportData
): Promise<string> => {
  const themeColor = '#EF5F55'; // sample uses coral red
  const darkText = '#333333';

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    defaultStyle: {
      fontSize: 10,
      color: darkText,
    },

    styles: {
      headerTitle: {
        fontSize: 12,
        // bold: true,
        marginBottom: 4,
      },
      headerSubtitle: {
        fontSize: 12,
        color: darkText,
        marginBottom: 10,
        bold: true,
      },
      sectionTitle: {
        fontSize: 24,
        bold: true,
        color: darkText,
        marginBottom: 6,
      },
      tableHeader: {
        fillColor: '#F4F6FA',
        color: 'gray',
        bold: true,
        fontSize: 10,
        margin: [0, 4],
      },
      tableCell: {
        margin: [0, 4],
        fontSize: 9,
      },
      badge: {
        color: themeColor,
        bold: true,
        fontSize: 18,
      },
      smallMuted: {
        fontSize: 8,
        italics: true,
      },
    },

    footer: () => ({
      text: 'The data presented in this report is for illustrative purposes and provides estimated savings.',
      alignment: 'center',
      fontSize: 9,
      color: '#666666',
      margin: [40, 0, 40, 20], // left, top, right, bottom
    }),

    content: [
      // PAGE 1 -------------------------------------
      {
        stack: [
          // HEADER TABLE WITH LOGO + TITLE + META
          {
            table: {
              widths: [80, '*', 200],
              body: [
                [
                  // LOGO + VERTICAL LINE
                  {
                    stack: [
                      {
                        image: logoBase64,
                        width: 70,
                        margin: [0, 0, 0, 4],
                      },
                    ],
                    border: [false, false, true, false], // ← RIGHT BORDER ENABLED
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    margin: [0, 0, 10, 0],
                  },

                  // TITLE + SUBTITLE
                  {
                    stack: [
                      {
                        text: 'Cut cloud costs with EazyOps:',
                        style: 'headerTitle',
                      },
                      { text: 'Simplicity, Savings.', style: 'headerSubtitle' },
                    ],
                    margin: [20, 0, 10, 0],
                    border: [false, false, false, false],
                  },

                  // REPORT META
                  {
                    alignment: 'right',
                    stack: [
                      {
                        text: [
                          { text: 'Report Date: ', bold: true },
                          { text: data.reportDate, color: 'gray' },
                          '\n\n',
                          { text: 'Generated By: ', bold: true },
                          { text: data.generatedBy, color: 'gray' },
                        ],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },
            layout: {
              defaultBorder: false, // disables outer table borders
            },
            margin: [0, 0, 0, 20],
          },

          // TITLE SECTION WITH CTA BUTTON
          {
            table: {
              widths: ['*', 'auto'],
              body: [
                [
                  // LEFT SIDE — TITLE + SUBTITLE
                  {
                    stack: [
                      {
                        text: 'EazyOps Cost Savings Analysis',
                        style: 'sectionTitle',
                        margin: [0, 20, 0, 4],
                      },
                      '\n',
                      {
                        text: 'Uncover Your Potential Savings With Our Platform',
                        margin: [0, 0, 0, 0],
                      },
                    ],
                    border: [false, false, false, false],
                  },

                  // RIGHT SIDE — ROUNDED BUTTON
                  {
                    width: 'auto',
                    stack: [
                      // Rounded background
                      {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 110,
                            h: 32,
                            r: 8,
                            color: '#6F9FE9',
                          },
                        ],
                      },

                      // Text ON TOP with EXACT vertical alignment
                      {
                        text: '$ Save Now',
                        alignment: 'center',
                        color: '#FFFFFF',
                        bold: true,
                        link: 'https://eazyops.cloud/sign-in',
                        margin: [0, -24, 0, 0], // perfectly centers text without breaking link
                        fontSize: 11,
                        width: 110,
                      },
                    ],
                    margin: [0, 30, 0, 0],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },

            layout: {
              defaultBorder: false,
            },

            margin: [0, 10, 0, 20],
          },

          // INFO TABLE + SAVINGS BADGE
          {
            table: {
              widths: ['*', '*', '*', '*'],
              body: [
                [
                  // CELL 1 (Top-left)
                  {
                    stack: [
                      { text: 'ACCOUNT NAME:', margin: [0, 0, 0, 2] },
                      { text: data.accountName, bold: true, fontSize: 13 },
                    ],
                    border: [false, false, false, false],
                  },
                  // CELL 2 (Top-right)
                  {
                    stack: [
                      { text: 'NO. OF RESOURCES:', margin: [0, 0, 0, 2] },
                      {
                        text: String(data.resourceCount),
                        bold: true,
                        fontSize: 13,
                      },
                    ],
                    border: [false, false, false, false],
                  },
                  // CELL 3 (Bottom-left)
                  {
                    stack: [
                      { text: 'REPORT TIMELINE:', margin: [0, 0, 0, 2] },
                      { text: data.timeline, bold: true, fontSize: 13 },
                    ],
                    border: [false, false, false, false],
                  },
                  // CELL 4 (Bottom-right)
                  {
                    stack: [
                      { text: 'TIMELINE DATE:', margin: [0, 0, 0, 2] },
                      { text: data.timelineDate, bold: true, fontSize: 13 },
                    ],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },

            layout: {
              defaultBorder: false,
              paddingLeft: () => 0,
              paddingRight: () => 0,
              paddingTop: () => 2,
              paddingBottom: () => 6,
            },

            margin: [0, 10, 0, 20],
          },

          {
            text: 'Savings With EazyOps',
            fontSize: 18,
            bold: true,
            color: darkText,
            margin: [0, 10, 0, 5],
          },

          {
            stack: [
              // BACKGROUND + BORDER (two rectangles)
              {
                canvas: [
                  // Filled background
                  {
                    type: 'rect',
                    x: 0,
                    y: 0,
                    w: 520,
                    h: 100,
                    r: 12,
                    color: '#E6FAF1',
                  },
                  // Border rectangle
                  {
                    type: 'rect',
                    x: 0,
                    y: 0,
                    w: 520,
                    h: 100,
                    r: 12,
                    lineColor: '#8BD6AF', // soft green border from screenshot
                    lineWidth: 1.4,
                  },
                ],
                margin: [0, 0, 0, -100],
              },

              // CONTENT (unchanged)
              {
                table: {
                  widths: ['*', '*', '*'],
                  body: [
                    [
                      {
                        stack: [
                          {
                            text: 'YOU CAN SAVE',
                            alignment: 'center',
                            margin: [0, 4, 0, 4],
                          },
                          {
                            text: `${data.savingsPercentage}%`,
                            alignment: 'center',
                            bold: true,
                            fontSize: 32,
                            margin: [0, 4, 0, 0],
                          },
                        ],
                        border: [false, false, false, false],
                      },

                      {
                        stack: [
                          {
                            text: 'MONTHLY SAVING',
                            alignment: 'center',
                            margin: [0, 4, 0, 4],
                          },
                          {
                            text: [
                              `$${roundToOneDecimal(data.monthlySaving ?? 0)}`,
                              { text: '/mo', color: 'gray', fontSize: 20 },
                            ],
                            alignment: 'center',
                            bold: true,
                            fontSize: 28,
                            color: '#333',
                            margin: [0, 4, 0, 0],
                          },
                        ],
                        border: [false, false, false, false],
                      },

                      {
                        stack: [
                          {
                            text: 'ANNUAL SAVING',
                            alignment: 'center',
                            margin: [0, 4, 0, 4],
                          },
                          {
                            text: [
                              `$${roundToOneDecimal(data.yearlySaving ?? 0)}`,
                              { text: '/y', color: 'gray', fontSize: 20 },
                            ],
                            alignment: 'center',
                            bold: true,
                            fontSize: 28,
                            color: '#333',
                            margin: [0, 4, 0, 0],
                          },
                        ],
                        border: [false, false, false, false],
                      },
                    ],
                  ],
                },
                layout: { defaultBorder: false },
                margin: [0, 16, 0, 0],
              },
            ],

            margin: [0, 0, 0, 20],
          },

          // Your Current Bill Section
          {
            text: 'Your Current Bill',
            fontSize: 18,
            bold: true,
            color: darkText,
            margin: [0, 10, 0, 5],
          },

          {
            stack: [
              // BACKGROUND + BORDER (two rounded rectangles)
              {
                canvas: [
                  // Filled background
                  {
                    type: 'rect',
                    x: 0,
                    y: 0,
                    w: 520,
                    h: 100,
                    r: 12,
                    color: '#F6F8FF', // light blue background
                  },
                  // Border
                  {
                    type: 'rect',
                    x: 0,
                    y: 0,
                    w: 520,
                    h: 100,
                    r: 12,
                    lineColor: '#D7E1FF', // soft blue border
                    lineWidth: 1.4,
                  },
                ],
                margin: [0, 0, 0, -100],
              },

              // CARD CONTENT
              {
                table: {
                  widths: ['*', '*'],
                  body: [
                    [
                      // LEFT COLUMN — MONTHLY SPEND
                      {
                        stack: [
                          {
                            text: 'MONTHLY SPEND',
                            bold: true,
                            alignment: 'center',
                            margin: [0, 8, 0, 8],
                          },
                          {
                            text: [
                              {
                                text: `$${roundToOneDecimal(
                                  data.monthlySpend ?? 0
                                )}`,
                                bold: true,
                                fontSize: 32,
                              },
                              { text: '/mo', color: 'gray', fontSize: 20 },
                            ],
                            alignment: 'center',
                          },
                        ],
                        border: [false, false, false, false],
                      },

                      // RIGHT COLUMN — YEARLY SPEND
                      {
                        stack: [
                          {
                            text: 'YEARLY SPEND',
                            bold: true,
                            alignment: 'center',
                            margin: [0, 8, 0, 8],
                          },
                          {
                            text: [
                              {
                                text: `~$${roundToOneDecimal(
                                  data.yearlySpend ?? 0
                                )}`,
                                bold: true,
                                fontSize: 32,
                              },
                              { text: '/y', color: 'gray', fontSize: 20 },
                            ],
                            alignment: 'center',
                          },
                        ],
                        border: [false, false, false, false],
                      },
                    ],
                  ],
                },
                layout: {
                  defaultBorder: false,
                },
                margin: [0, 16, 0, 0],
              },
            ],

            margin: [0, 0, 0, 30],
          },

          { text: '', pageBreak: 'after' },
        ],
      },

      // PAGE 2 -------------------------------------
      {
        stack: [
          // HEADER TABLE WITH LOGO + TITLE + META
          {
            table: {
              widths: [80, '*', 200],
              body: [
                [
                  // LOGO + VERTICAL LINE
                  {
                    stack: [
                      {
                        image: logoBase64,
                        width: 70,
                        margin: [0, 0, 0, 4],
                      },
                    ],
                    border: [false, false, true, false], // ← RIGHT BORDER ENABLED
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    margin: [0, 0, 10, 0],
                  },

                  // TITLE + SUBTITLE
                  {
                    stack: [
                      {
                        text: 'Cut cloud costs with EazyOps:',
                        style: 'headerTitle',
                      },
                      { text: 'Simplicity, Savings.', style: 'headerSubtitle' },
                    ],
                    margin: [20, 0, 10, 0],
                    border: [false, false, false, false],
                  },

                  // REPORT META
                  {
                    alignment: 'right',
                    stack: [
                      {
                        text: [
                          { text: 'Report Date: ', bold: true },
                          { text: data.reportDate, color: 'gray' },
                          '\n\n',
                          { text: 'Generated By: ', bold: true },
                          { text: data.generatedBy, color: 'gray' },
                        ],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },
            layout: {
              defaultBorder: false, // disables outer table borders
            },
            margin: [0, 0, 0, 20],
          },

          {
            stack: [
              {
                text: 'Project List',
                style: 'sectionTitle',
                margin: [0, 10, 0, 4],
              },
              '\n',
              {
                text: 'This report will summarize the anticipated cost-saving initiatives and optimizations for each resource type.',
                margin: [0, 0, 0, 10],
              },
            ],
            border: [false, false, false, false],
          },

          {
            table: {
              widths: ['*', 80, 100, 140], // adjust as needed
              headerRows: 1,

              body: [
                // HEADER ROW
                [
                  { text: 'Project Name', style: 'tableHeader' },
                  {
                    text: 'Resource Count',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                  {
                    text: 'Monthly Spend',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                  {
                    text: 'Monthly Saving',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                ],

                // DYNAMIC CONTENT ROWS
                ...data.projects.map((p) => [
                  { text: p.name, margin: [4, 6, 4, 6], fillColor: '#F4F6FA' },
                  {
                    text: String(p.resourceCount),
                    alignment: 'center',
                    margin: [4, 6, 4, 6],
                    fillColor: '#F4F6FA',
                  },
                  {
                    text: `$${roundToOneDecimal(p.monthlySpend ?? 0)}`,
                    alignment: 'center',
                    margin: [4, 6, 4, 6],
                    fillColor: '#F4F6FA',
                  },

                  // MONTHLY SAVING CELL — matches green highlight
                  {
                    stack: [
                      {
                        text: [
                          `$${roundToOneDecimal(p.monthlySaving ?? 0)}`,
                          '    ',
                          {
                            text: `${p.savingPercent}%`,
                            color: '#2E9E4D',
                            fontSize: 10,
                          },
                        ],
                        bold: true,
                        color: '#000',
                        margin: [0, 0, 0, 2],
                      },
                    ],
                    fillColor: '#E6FAF1', // greenish background
                    alignment: 'center',
                    margin: [0, 6, 0, 6],
                  },
                ]),
              ],
            },

            layout: {
              fillColor: (rowIndex: number) => {
                // row 0 = header
                if (rowIndex === 0) return '#F4F6FA'; // header background
                return null; // normal rows
              },

              hLineColor: () => '#D8DEE5',
              vLineColor: () => '#D8DEE5',
              hLineWidth: (i: number) => (i === 1 ? 1 : 0.8),
              vLineWidth: () => 0,

              paddingLeft: () => 8,
              paddingRight: () => 8,
              paddingTop: () => 8,
              paddingBottom: () => 8,
            },

            margin: [0, 0, 0, 20],
          },

          { text: '', pageBreak: 'after' },
        ],
      },

      // PAGE 3 -------------------------------------
      {
        stack: [
          // HEADER TABLE WITH LOGO + TITLE + META
          {
            table: {
              widths: [80, '*', 200],
              body: [
                [
                  // LOGO + VERTICAL LINE
                  {
                    stack: [
                      {
                        image: logoBase64,
                        width: 70,
                        margin: [0, 0, 0, 4],
                      },
                    ],
                    border: [false, false, true, false], // ← RIGHT BORDER ENABLED
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    margin: [0, 0, 10, 0],
                  },

                  // TITLE + SUBTITLE
                  {
                    stack: [
                      {
                        text: 'Cut cloud costs with EazyOps:',
                        style: 'headerTitle',
                      },
                      { text: 'Simplicity, Savings.', style: 'headerSubtitle' },
                    ],
                    margin: [20, 0, 10, 0],
                    border: [false, false, false, false],
                  },

                  // REPORT META
                  {
                    alignment: 'right',
                    stack: [
                      {
                        text: [
                          { text: 'Report Date: ', bold: true },
                          { text: data.reportDate, color: 'gray' },
                          '\n\n',
                          { text: 'Generated By: ', bold: true },
                          { text: data.generatedBy, color: 'gray' },
                        ],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },
            layout: {
              defaultBorder: false, // disables outer table borders
            },
            margin: [0, 0, 0, 20],
          },
          { text: 'Resources Summary', style: 'sectionTitle' },
          {
            text: 'This report will summarize the anticipated cost-saving initiatives and optimizations for each resource type.',
            margin: [0, 0, 0, 10],
          },

          {
            table: {
              headerRows: 1,
              widths: [30, '*', 80, 80, 80, 80],

              body: [
                // HEADER
                [
                  { text: '', style: 'tableHeader' },
                  { text: 'Resource Name', style: 'tableHeader' },
                  {
                    text: 'Monthly Cost',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                  {
                    text: 'Monthly Savings',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                  {
                    text: 'Yearly Cost',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                  {
                    text: 'Yearly Savings',
                    style: 'tableHeader',
                    alignment: 'center',
                  },
                ],

                // ROWS — HARD-CODED ICONS + USING resourcesSummary DATA
                ...data.resourcesSummary.map((r) => {
                  const savingPercent =
                    r.monthlyCost === 0
                      ? 0
                      : Math.round((r.monthlySavings / r.monthlyCost) * 100);

                  return [
                    // ICON (hardcoded)
                    (() => {
                      switch (r.name) {
                        case 'Volume':
                          return {
                            image: iconVolumeBase64,
                            width: 18,
                            margin: [0, 4],
                          };
                        case 'Unused IPs':
                          return {
                            image: iconUnusedIPsBase64,
                            width: 18,
                            margin: [0, 4],
                          };
                        case 'Volume Snapshots':
                          return {
                            image: iconSnapshotsBase64,
                            width: 18,
                            margin: [0, 4],
                          };
                        case 'Database':
                          return {
                            image: iconDatabaseBase64,
                            width: 18,
                            margin: [0, 4],
                          };
                        default:
                          return '';
                      }
                    })(),

                    // Resource Name
                    { text: r.name, margin: [0, 6, 0, 6] },

                    // Monthly Cost
                    {
                      text: `$${roundToOneDecimal(r.monthlyCost ?? 0)}`,
                      alignment: 'center',
                      margin: [0, 6, 0, 6],
                    },

                    // Monthly Savings cell (green)
                    {
                      stack: [
                        {
                          text: `$${roundToOneDecimal(r.monthlySavings ?? 0)}`,
                          bold: true,
                          alignment: 'center',
                          margin: [0, 0, 0, 2],
                        },
                        {
                          columns: [
                            {
                              image: arrowDownBase64,
                              width: 8,
                              margin: [0, 0, 2, 0],
                            },
                            {
                              text: `${savingPercent}%`,
                              color: '#2E9E4D',
                              fontSize: 10,
                            },
                          ],
                          alignment: 'center',
                        },
                      ],
                      fillColor: '#E6FAF1',
                      alignment: 'center',
                      margin: [0, 6, 0, 6],
                    },

                    // Yearly Cost
                    {
                      text: `$${roundToOneDecimal(r.yearlyCost ?? 0)}`,
                      alignment: 'center',
                      margin: [0, 6, 0, 6],
                    },

                    // Yearly Savings (green)
                    {
                      stack: [
                        {
                          text: `$${roundToOneDecimal(r.yearlySavings ?? 0)}`,
                          bold: true,
                          alignment: 'center',
                          margin: [0, 0, 0, 2],
                        },
                        {
                          columns: [
                            {
                              image: arrowDownBase64,
                              width: 8,
                              margin: [0, 0, 2, 0],
                            },
                            {
                              text: `${savingPercent}%`,
                              color: '#2E9E4D',
                              fontSize: 10,
                            },
                          ],
                          alignment: 'center',
                        },
                      ],
                      fillColor: '#E6FAF1',
                      alignment: 'center',
                      margin: [0, 6, 0, 6],
                    },
                  ];
                }),

                // TOTAL ROW
                [
                  { text: '', fillColor: '#EEF2FF' },
                  { text: 'Total Savings', bold: true, fillColor: '#EEF2FF' },

                  // Monthly cost total
                  {
                    text: `$${roundToOneDecimal(data.monthlySpend ?? 0)}/m`,
                    bold: true,
                    alignment: 'center',
                    fillColor: '#EEF2FF',
                  },

                  // Monthly saving total (green)
                  {
                    stack: [
                      {
                        text: `$${roundToOneDecimal(
                          data.monthlySaving ?? 0
                        )}/m`,
                        bold: true,
                        alignment: 'center',
                      },
                      {
                        columns: [
                          { image: arrowDownBase64, width: 8 },
                          {
                            text: `${data.savingsPercentage}%`,
                            color: '#2E9E4D',
                            fontSize: 10,
                          },
                        ],
                        alignment: 'center',
                      },
                    ],
                    fillColor: '#E6FAF1',
                  },

                  // Yearly cost total
                  {
                    text: `$${roundToOneDecimal(data.yearlySpend ?? 0)}/y`,
                    bold: true,
                    alignment: 'center',
                    fillColor: '#EEF2FF',
                  },

                  // Yearly saving total (green)
                  {
                    stack: [
                      {
                        text: `$${roundToOneDecimal(data.yearlySaving ?? 0)}/y`,
                        bold: true,
                        alignment: 'center',
                      },
                      {
                        columns: [
                          { image: arrowDownBase64, width: 8 },
                          {
                            text: `${data.savingsPercentage}%`,
                            color: '#2E9E4D',
                            fontSize: 10,
                          },
                        ],
                        alignment: 'center',
                      },
                    ],
                    fillColor: '#E6FAF1',
                  },
                ],
              ],
            },

            layout: {
              hLineColor: () => '#D8E0E5',
              vLineColor: () => '#D8E0E5',
              hLineWidth: () => 0.8,
              vLineWidth: () => 0,
              fillColor: (i: number) => (i === 0 ? '#F4F6FA' : null),
              paddingLeft: () => 8,
              paddingRight: () => 8,
              paddingTop: () => 8,
              paddingBottom: () => 8,
            },
          },

          {
            text: 'Strategic Recommendations',
            style: 'sectionTitle',
            margin: [0, 20, 0, 5],
          },

          {
            text: 'Immediate Actions',
            bold: true,
            margin: [0, 5, 0, 5],
            fontSize: 16,
          },
          {
            ul: data.recommendations.immediate,
            margin: [0, 5, 0, 20],
            fontSize: 14,
          },

          {
            text: 'Mid-Term Actions',
            bold: true,
            margin: [0, 5, 0, 5],
            fontSize: 16,
          },
          {
            ul: data.recommendations.midTerm,
            margin: [0, 5, 0, 20],
            fontSize: 14,
          },

          {
            text: 'Long-Term Actions',
            bold: true,
            margin: [0, 5, 0, 5],
            fontSize: 16,
          },
          {
            ul: data.recommendations.longTerm,
            margin: [0, 5, 0, 20],
            fontSize: 14,
          },

          { text: '', pageBreak: 'after' },
        ],
      },

      // PAGE 4 -------------------------------------
      {
        stack: [
          // HEADER TABLE WITH LOGO + TITLE + META
          {
            table: {
              widths: [80, '*', 200],
              body: [
                [
                  // LOGO + VERTICAL LINE
                  {
                    stack: [
                      {
                        image: logoBase64,
                        width: 70,
                        margin: [0, 0, 0, 4],
                      },
                    ],
                    border: [false, false, true, false], // ← RIGHT BORDER ENABLED
                    borderColor: '#CCCCCC',
                    borderWidth: 1,
                    margin: [0, 0, 10, 0],
                  },

                  // TITLE + SUBTITLE
                  {
                    stack: [
                      {
                        text: 'Cut cloud costs with EazyOps:',
                        style: 'headerTitle',
                      },
                      { text: 'Simplicity, Savings.', style: 'headerSubtitle' },
                    ],
                    margin: [20, 0, 10, 0],
                    border: [false, false, false, false],
                  },

                  // REPORT META
                  {
                    alignment: 'right',
                    stack: [
                      {
                        text: [
                          { text: 'Report Date: ', bold: true },
                          { text: data.reportDate, color: 'gray' },
                          '\n\n',
                          { text: 'Generated By: ', bold: true },
                          { text: data.generatedBy, color: 'gray' },
                        ],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },
            layout: {
              defaultBorder: false, // disables outer table borders
            },
            margin: [0, 0, 0, 20],
          },

          // SECTION: IMPLEMENTATION COMPLEXITY
          { text: 'Implementation Complexity', style: 'sectionTitle' },

          {
            table: {
              headerRows: 1,
              widths: [30, '*', '*'],

              body: [
                // HEADER
                [
                  { text: '', style: 'tableHeader' },
                  { text: 'Category', style: 'tableHeader' },
                  { text: 'Complexity', style: 'tableHeader' },
                ],

                // ROWS — HARD-CODED ICONS + USING resourcesSummary DATA
                ...data.complexity.map((r) => [
                  // ICON (hardcoded)
                  (() => {
                    switch (r.name) {
                      case 'Volumes':
                        return {
                          image: iconVolumeBase64,
                          width: 18,
                          margin: [0, 4],
                        };
                      case 'Unused IPs':
                        return {
                          image: iconUnusedIPsBase64,
                          width: 18,
                          margin: [0, 4],
                        };
                      case 'Volume Snapshots':
                        return {
                          image: iconSnapshotsBase64,
                          width: 18,
                          margin: [0, 4],
                        };
                      case 'Databases':
                        return {
                          image: iconDatabaseBase64,
                          width: 18,
                          margin: [0, 4],
                        };
                      default:
                        return '';
                    }
                  })(),

                  // Resource Name
                  { text: r.name, margin: [0, 6, 0, 6] },

                  // Complexity
                  {
                    text: r.level,
                    alignment: 'center',
                    margin: [0, 6, 0, 6],
                  },
                ]),
              ],
            },

            layout: {
              hLineColor: () => '#D8E0E5',
              vLineColor: () => '#D8E0E5',
              hLineWidth: () => 0.8,
              vLineWidth: () => 0,
              fillColor: (i: number) => (i === 0 ? '#F4F6FA' : null),
              paddingLeft: () => 8,
              paddingRight: () => 8,
              paddingTop: () => 8,
              paddingBottom: () => 8,
            },
          },
        ],
      },
    ],
  };

  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pdfMake.createPdf(docDefinition as any).getDataUrl((url) => resolve(url));
  });
};
